"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { decodeGameAnalysisListResponse, type GameAnalysisListResponse, type AnalysisFieldChange, type AnalysisFlag } from "./contracts";
import { isMetadataAcknowledgement, readFlagError, uncertainFlagMessage } from "./flagFeedback";
import type { useGameAnalysisAuthorization } from "./useGameAnalysisAuthorization";

export type CommentSaveResult = { status: "confirmed" } | { status: "rejected"; message: string } | { status: "recovered"; matches: boolean } | { status: "unavailable" };

/** Own the current authorized collection, row saves, and overlapping import/read recovery. */
export function useGameAnalysisCollection(selected: boolean, access: ReturnType<typeof useGameAnalysisAuthorization>, onAuthoritativeRead?: (data: GameAnalysisListResponse) => void) {
  const authoritativeRead = useRef(onAuthoritativeRead);
  authoritativeRead.current = onAuthoritativeRead;
  const accessRef = useRef(access);
  accessRef.current = access;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const [loaded, setLoaded] = useState<{
    generation: object;
    data: GameAnalysisListResponse;
  }>();
  const loadedRef = useRef(loaded);
  loadedRef.current = loaded;
  const [failure, setFailure] = useState<object>();
  const [needsRefresh, setNeedsRefresh] = useState<object>();
  const [retryVersion, setRetryVersion] = useState(0);
  const [saving, setSaving] = useState<ReadonlySet<number>>(new Set());
  const [rowErrors, setRowErrors] = useState<ReadonlyMap<number, string>>(new Map());
  const [recovering, setRecovering] = useState<ReadonlySet<number>>(new Set());
  const forceRead = useRef(false);
  const activeRead = useRef<AbortController>();
  const pendingGames = useRef(new Set<number>());
  const activeWrites = useRef(new Set<object>());
  const cancelSaves = useRef(new Map<number, () => void>());
  const recoveryFocus = useRef(new Map<number, (data: GameAnalysisListResponse) => void>());
  const startWrite = () => {
    const token = {};
    activeWrites.current.add(token);
    listEpoch.current += 1;
    if (activeRead.current) {
      forceRead.current = true;
      activeRead.current.abort();
    }
    return () => {
      if (!activeWrites.current.delete(token) || !access.isCurrent()) return;
      listEpoch.current += 1;
      if (activeWrites.current.size === 0 && forceRead.current) setRetryVersion((version) => version + 1);
    };
  };
  const saveMetadata = (problemId: number, intent: AnalysisFieldChange, onConfirmed: () => void, onRejected?: (message: string) => void, onRecovered?: (data: GameAnalysisListResponse) => void, onUnavailable?: () => void) => {
    if (!access.isCurrent() || pendingGames.current.has(problemId) || recovering.has(problemId)) return;
    const release = startWrite();
    pendingGames.current.add(problemId);
    setSaving(new Set(pendingGames.current));
    setRowErrors((errors) => { const next = new Map(errors); next.delete(problemId); return next; });
    const controller = new AbortController();
    let settled = false;
    const deadline = performance.now() + 30000;
    const current = () => !settled && selectedRef.current && access.isCurrent();
    const recover = () => {
      recoveryFocus.current.set(problemId, onRecovered ?? onConfirmed);
      setRecovering((games) => new Set(games).add(problemId));
      setRowErrors((errors) => new Map(errors).set(problemId, uncertainFlagMessage));
      forceRead.current = true;
      listEpoch.current += 1;
      setRetryVersion((version) => version + 1);
    };
    const finish = () => {
      settled = true;
      window.clearTimeout(timer);
      pendingGames.current.delete(problemId);
      cancelSaves.current.delete(problemId);
      setSaving(new Set(pendingGames.current));
      release();
    };
    cancelSaves.current.set(problemId, () => { controller.abort(); finish(); });
    const expire = () => {
      if (!current()) return;
      controller.abort();
      finish();
      recover();
    };
    const withinDeadline = () => {
      if (!current()) return false;
      if (performance.now() >= deadline) { expire(); return false; }
      return true;
    };
    const timer = window.setTimeout(expire, 30000);
    void fetch(`/api/game-analysis/${problemId}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ [intent.field]: intent.value ?? "" }),
      signal: controller.signal,
    }).then(async (result) => {
      if (!withinDeadline()) return;
      const acknowledgement: unknown = await result.json();
      if (!withinDeadline()) return;
      if (result.status !== 200) {
        const error = readFlagError(result.status, acknowledgement);
        if (!withinDeadline()) return;
        if (error?.code === "unauthenticated") { access.refreshSession(); return; }
        if (error?.code === "forbidden") { access.invalidate(); return; }
        if (error?.code === "not-found") { onUnavailable?.(); refreshList(); return; }
        if (error && error.code !== "outcome-unknown") {
          setRowErrors((errors) => new Map(errors).set(problemId, error.message));
          onRejected?.(error.message);
        }
        else recover();
        return;
      }
      const valid = isMetadataAcknowledgement(acknowledgement, problemId, intent);
      if (!withinDeadline()) return;
      if (!valid) { recover(); return; }
      onConfirmed();
      setLoaded((current) => current && ({ ...current, data: { ...current.data, games: current.data.games.map((game) => game.problemId === problemId ? { ...game, [intent.field]: intent.value } : game) } }));
    }).catch(() => { if (withinDeadline()) recover(); }).finally(() => { if (current()) finish(); });
  };
  const { generation, allowed, isCurrent } = access;
  const listEpoch = useRef(0);
  useLayoutEffect(() => () => {
    if (activeWrites.current.size > 0) {
      listEpoch.current += 1;
      forceRead.current = true;
      loadedRef.current = undefined;
      setLoaded(undefined);
    }
    for (const cancel of cancelSaves.current.values()) cancel();
    activeWrites.current.clear();
    recoveryFocus.current.clear();
  }, [generation, selected]);
  const invalidateList = () => {
    if (!access.isCurrent()) return;
    listEpoch.current += 1;
    loadedRef.current = undefined;
    setLoaded(undefined);
    setFailure(undefined);
    setNeedsRefresh(generation);
  };
  const refreshList = () => {
    if (!access.isCurrent()) return;
    invalidateList();
    setNeedsRefresh(undefined);
    forceRead.current = true;
    setRetryVersion((version) => version + 1);
  };
  const startImport = () => {
    const release = startWrite();
    invalidateList();
    return release;
  };

  useEffect(() => {
    if (!selected || !allowed) return;
    if (activeWrites.current.size > 0) return;
    if (loadedRef.current?.generation === generation && !forceRead.current) return;
    forceRead.current = false;
    setNeedsRefresh(undefined);
    setFailure(undefined);
    const operationAccess = accessRef.current;
    const controller = new AbortController();
    activeRead.current = controller;
    const epoch = listEpoch.current;
    let cancelled = false;
    const current = () =>
      !cancelled && epoch === listEpoch.current && selectedRef.current && isCurrent();
    void (async () => {
      try {
        const result = await fetch("/api/game-analysis", {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!current()) return;
        if (result.status === 401) {
          operationAccess.refreshSession();
          return;
        }
        if (result.status === 403) {
          operationAccess.invalidate();
          return;
        }
        if (!result.ok) {
          setFailure(generation);
          return;
        }
        const data = decodeGameAnalysisListResponse(await result.json());
        if (current()) {
          for (const confirmFocus of recoveryFocus.current.values()) confirmFocus(data);
          recoveryFocus.current.clear();
          authoritativeRead.current?.(data);
          setLoaded({ generation, data });
          setRecovering(new Set());
          setRowErrors(new Map());
        }
      } catch {
        if (current()) setFailure(generation);
      } finally {
        if (activeRead.current === controller) activeRead.current = undefined;
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
      if (activeRead.current === controller) {
        forceRead.current = true;
        activeRead.current = undefined;
      }
    };
  }, [selected, allowed, generation, isCurrent, retryVersion]);

  const saveFlag = (problemId: number, flag: AnalysisFlag, value: boolean, onConfirmed: () => void) => saveMetadata(problemId, { field: flag, value }, onConfirmed);
  const saveComment = (problemId: number, value: string | null, onResult: (result: CommentSaveResult) => void) => saveMetadata(problemId, { field: "userComment", value }, () => onResult({ status: "confirmed" }), (message) => onResult({ status: "rejected", message }), (data) => {
    const game = data.games.find((game) => game.problemId === problemId);
    if (game) onResult({ status: "recovered", matches: game.userComment === value });
  }, () => onResult({ status: "unavailable" }));
  return { loaded, failure, needsRefresh, saving, rowErrors, recovering, saveFlag, saveComment, refreshList, startImport };
}
